import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from "@/components/ui/accordion";
import { AddSubsectionRow } from "./AddSubSectionRow";
import { ItemHeader } from "./ItemHeader";
import { SubsectionAccordion } from "./SubsectionAccordion";
import { Button } from "@/components/ui/button";
import { Settings, Trash } from "lucide-react";
import { useState } from "react";
import { cn } from "@client/lib";
import { ConditionalBuilder } from "./ConditionalBuilder";

interface SectionAccordionProps {
    section: PolicyFormSection;
    questions: Record<string, PolicyFormQuestion>;
    onSectionChange: (sectionId: string, updates: Partial<PolicyFormSection>) => void;
    onSubsectionChange: (
        sectionId: string,
        subsectionId: string,
        updates: Partial<PolicyFormSubSection>
    ) => void;
    onQuestionGroupChange: (
        sectionId: string,
        subsectionId: string,
        groupId: string,
        updates: Partial<PolicyFormQuestionGroup>
    ) => void;
    onQuestionChange: (questionId: string, updates: Partial<PolicyFormQuestion>) => void;
    onRemoveQuestion: (
        sectionId: string,
        subsectionId: string,
        groupId: string,
        questionId: string
    ) => void;
    onAddSubsection: (sectionId: string, subsection: PolicyFormSubSection) => void;
    onAddGroup: (sectionId: string, subsectionId: string, group: PolicyFormQuestionGroup) => void;
    onAddQuestionToGroup: (
        sectionId: string,
        subsectionId: string,
        groupId: string,
        question: PolicyFormQuestion
    ) => void;
    onToggleConditional?: () => void;
    onRemoveGroup: (
        sectionId: string,
        subsectionId: string,
        groupId: string
    ) => void;
    onRemoveSubsection: (
        sectionId: string,
        subsectionId: string
    ) => void;
    onRemoveSection: (
        sectionId: string
    ) => void;
}

export function SectionAccordion({ section, onSectionChange, onRemoveGroup, onRemoveSubsection, onRemoveSection, ...props }: SectionAccordionProps) {
    const [isConditionOpen, setIsConditionOpen] = useState(false);

    return (
        <Accordion type="multiple" className="space-y-3">
            <AccordionItem value={section.id} className="border rounded-lg shadow-sm">
                <AccordionTrigger className="px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 text-gray-900">
                    <ItemHeader
                        title={section.title}
                        onTitleChange={(t) => onSectionChange(section.id, { title: t })}
                        onToggleConditional={props.onToggleConditional}
                    />

                    <button type="button" onClick={() => {
                        setIsConditionOpen(true);
                    }}>
                        <Settings className={cn("h-4 w-4 flex-shrink-0", section.conditional ? "text-green-500" : "text-gray-500 ")} />
                    </button>

                    <ConditionalBuilder
                        open={isConditionOpen}
                        title={`Conditional Logic - Section`}
                        visibleCondition={section.conditional?.visible}
                        requiredCondition={section.conditional?.required}
                        onChangeVisible={(c) =>
                            onSectionChange(section.id, { conditional: { ...(section.conditional ?? {}), visible: c } })
                        }
                        onChangeRequired={(c) =>
                            onSectionChange(section.id, { conditional: { ...(section.conditional ?? {}), required: c } })
                        }
                        onClose={() => {
                            setIsConditionOpen(false)
                        }}
                        questions={props.questions}
                    />

                    <Button variant="ghost" size="sm" onClick={() => onRemoveSection(section.id)}>
                        <Trash size={14} className="text-red-500" />
                    </Button>
                </AccordionTrigger>

                <AccordionContent className="px-4 py-3 space-y-4">
                    {section.subsections.map((sub) => (
                        <SubsectionAccordion
                            key={sub.id}
                            sectionId={section.id}
                            subsection={sub}
                            onRemoveGroup={onRemoveGroup}
                            onRemoveSubsection={onRemoveSubsection}
                            {...props}
                        />
                    ))}
                    <AddSubsectionRow sectionId={section.id} onAddSubsection={props.onAddSubsection} />
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}